/**
 * RulesDialog - Manage custom LLM rules for Bank Entry
 */

import { useEffect, useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '../../components/ui/dialog';
import { Button } from '../../components/ui/button';
import { Textarea } from '../../components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import { Pencil, X, Plus } from 'lucide-react';

const API_BASE = import.meta.env.VITE_API_URL;
const RULE_MAX_LENGTH = 150;

interface BankRule {
  id: string;
  company: string;
  ruleText: string;
  targetType: string;
}

interface RulesDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export const RulesDialog: React.FC<RulesDialogProps> = ({ open, onOpenChange }) => {
  const [rules, setRules] = useState<BankRule[]>([]);
  const [companies, setCompanies] = useState<string[]>([]);
  const [showForm, setShowForm] = useState(false);
  const [editingRuleId, setEditingRuleId] = useState<string | null>(null);
  const [company, setCompany] = useState('');
  const [ruleText, setRuleText] = useState('');
  const [isSaving, setIsSaving] = useState(false);

  const token = localStorage.getItem('auth_token');

  const resetForm = () => {
    setEditingRuleId(null);
    setCompany('');
    setRuleText('');
    setShowForm(false);
  };

  const fetchRules = async () => {
    try {
      const response = await fetch(`${API_BASE}/api/bank-entry/rules`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!response.ok) return;
      const data = await response.json();
      setRules(data.rules || []); // Fixed: Extract rules array from response object
    } catch (error) {
      console.error('Failed to fetch bank rules', error);
    }
  };

  const fetchCompanies = async () => {
    if (companies.length > 0) return;
    try {
      const response = await fetch(`${API_BASE}/api/bank-entry/companies`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!response.ok) return;
      const data = await response.json();
      setCompanies(data || []);
    } catch (error) {
      console.error('Failed to fetch companies', error);
    }
  };

  useEffect(() => {
    if (!open) return;
    fetchRules();
  }, [open]);

  const handleAddClick = () => {
    setEditingRuleId(null);
    setCompany('');
    setRuleText('');
    setShowForm(true);
    fetchCompanies();
  };

  const handleEdit = (rule: BankRule) => {
    setEditingRuleId(rule.id);
    setCompany(rule.company);
    setRuleText(rule.ruleText);
    setShowForm(true);
    fetchCompanies();
  };

  const handleDelete = async (ruleId: string) => {
    await fetch(`${API_BASE}/api/bank-entry/rules/${ruleId}`, {
      method: 'DELETE',
      headers: { Authorization: `Bearer ${token}` },
    });
    fetchRules();
  };

  const handleSave = async () => {
    const trimmed = ruleText.trim();
    if (!company || !trimmed) return;

    setIsSaving(true);
    try {
      const payload = {
        company,
        ruleText: trimmed,
      };

      if (editingRuleId) {
        await fetch(`${API_BASE}/api/bank-entry/rules/${editingRuleId}`, {
          method: 'PUT',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify(payload),
        });
      } else {
        await fetch(`${API_BASE}/api/bank-entry/rules`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify(payload),
        });
      }

      await fetchRules();
      resetForm();
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle>Bank-Entry Rules</DialogTitle>
        </DialogHeader>

        <div className="space-y-4">
          <div className="space-y-3">
            <div className="flex items-center justify-between">
              <Button
                variant="outline"
                onClick={() => {
                  if (!showForm) {
                    handleAddClick();
                  } else {
                    setShowForm(false);
                  }
                }}
              >
                <Plus className="h-4 w-4 mr-2" /> Add Rule
              </Button>
            </div>

            {showForm && (
              <div className="rounded-lg border p-3 space-y-3">
              <div className="space-y-2">
                <label className="text-sm font-medium">Company</label>
                <Select value={company} onValueChange={setCompany}>
                  <SelectTrigger>
                    <SelectValue placeholder="Select company" />
                  </SelectTrigger>
                  <SelectContent>
                    {companies.map((item) => (
                      <SelectItem key={item} value={item}>
                        {item}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <label className="text-sm font-medium">Rule</label>
                <Textarea
                  value={ruleText}
                  onChange={(event) => setRuleText(event.target.value)}
                  maxLength={RULE_MAX_LENGTH}
                  placeholder="Enter rule (max 150 characters)"
                />
                <div className="text-xs text-muted-foreground text-right">
                  {ruleText.length}/{RULE_MAX_LENGTH}
                </div>
              </div>

              <div className="flex items-center gap-2">
                <Button onClick={handleSave} disabled={!company || !ruleText.trim() || isSaving}>
                  {editingRuleId ? 'Update Rule' : 'Save Rule'}
                </Button>
                <Button variant="ghost" onClick={resetForm}>
                  Cancel
                </Button>
              </div>
              </div>
            )}
          </div>

          <div className="border rounded-lg overflow-hidden w-full">
            <div className="overflow-x-auto">
              <table className="w-full text-sm table-fixed">
                <thead className="bg-muted">
                  <tr>
                    <th className="p-2 text-left whitespace-nowrap w-1/4">Company</th>
                    <th className="p-2 text-left w-1/2">Rule</th>
                    <th className="p-2 text-right whitespace-nowrap w-1/4">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {rules.length === 0 ? (
                    <tr>
                      <td className="p-3 text-sm text-muted-foreground" colSpan={3}>
                        No rules created yet.
                      </td>
                    </tr>
                  ) : (
                    rules.map((rule) => (
                      <tr key={rule.id} className="border-t">
                        <td className="p-2 text-sm whitespace-nowrap overflow-hidden text-ellipsis">{rule.company}</td>
                        <td className="p-2 text-sm text-muted-foreground overflow-hidden text-ellipsis" title={rule.ruleText}>
                          {rule.ruleText}
                        </td>
                        <td className="p-2">
                          <div className="flex items-center justify-end gap-1">
                            <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => handleEdit(rule)}>
                              <Pencil className="h-4 w-4" />
                            </Button>
                            <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => handleDelete(rule.id)}>
                              <X className="h-4 w-4" />
                            </Button>
                          </div>
                        </td>
                      </tr>
                    ))
                  )}
              </tbody>
              </table>
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default RulesDialog;
